import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Subscription } from 'rxjs';
import { MessageService } from '../../../services/message-service/message.service';
import { EnterValueDialogComponent } from '../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../dialogs/enter-value-dialog/enter-value-response';
import { GruppenService } from '../../gruppen/gruppen.service';
import { GruppeTier } from '../classes/gruppe-tier';
import { Tierbemerkung } from '../classes/tierbemerkung';
import { GruppeTierService } from '../gruppe-tier.service';

interface BemerkungOption {
    text: string;
    checked: boolean;
}

@Component({
    selector: 'kiene-gruppe-tier-bemerkung',
    templateUrl: './gruppe-tier-bemerkung.component.html',
    styleUrls: ['./gruppe-tier-bemerkung.component.scss'],
})
export class GruppeTierBemerkungComponent implements OnInit, OnDestroy {
    @Input('dialog') dialog = false;
    @Input('tier') tier: GruppeTier;

    @Output('saved')
    saved: EventEmitter<boolean> = new EventEmitter<boolean>();

    isLoading = true;
    isSaving = false;
    ueberschrift = 'Bemerkungen hinzufügen';
    optionen: BemerkungOption[] = [];

    private initialTexts = new Set<string>();
    private gruppeTierSubscription: Subscription;
    private optionenSub: Subscription;

    constructor(
        private gruppenService: GruppenService,
        public messageService: MessageService,
        public gruppeTierService: GruppeTierService,
        private dialogService: MatDialog,
    ) {}

    ngOnInit(): void {
        this.loadTier();
    }

    ngOnDestroy(): void {
        this.gruppeTierSubscription?.unsubscribe();
        this.optionenSub?.unsubscribe();
    }

    openCreateDialog(): void {
        if (this.isSaving) {
            return;
        }

        const input = new EnterValueInput();
        input.title = 'Bemerkung anlegen';
        input.addInputValue('Bezeichnung', 'text');

        this.dialogService
            .open(EnterValueDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: (result: EnterValueResponse) => {
                    const text = result?.getValue(0)?.trim?.();
                    if (!result?.result || !text) {
                        return;
                    }
                    const existing = this.optionen.find(option => option.text === text);
                    if (existing) {
                        existing.checked = true;
                        return;
                    }
                    this.optionen = [{ text, checked: true }, ...this.optionen];
                },
            });
    }

    toggleOption(change: MatCheckboxChange | { checked: boolean }, option: BemerkungOption, click?: MouseEvent): void {
        if (click) {
            click.preventDefault();
            click.stopPropagation();
        }
        option.checked = change.checked;
    }

    save(): void {
        if (!this.tier?.gt_id || this.isSaving) {
            return;
        }

        const checked = new Set(this.optionen.filter(option => option.checked).map(option => option.text));
        const toAdd = this.optionen.filter(option => option.checked && !this.initialTexts.has(option.text));
        const toRemove = (this.tier.bemerkungen ?? []).filter(bemerkung => {
            const text = (bemerkung?.bemerkung ?? '').trim();
            return text && this.initialTexts.has(text) && !checked.has(text);
        });

        if (toAdd.length === 0 && toRemove.length === 0) {
            this.saved.emit(true);
            return;
        }

        this.isSaving = true;
        const requests = [
            ...toRemove.map(bemerkung => this.gruppeTierService.deleteBemerkung(bemerkung)),
            ...toAdd.map(option =>
                this.gruppeTierService.createOrUpdateBemerkung(
                    new Tierbemerkung({ bemerkung: option.text, gt_id: this.tier.gt_id }),
                ),
            ),
        ];

        forkJoin(requests).subscribe({
            next: () => {
                this.isSaving = false;
                if (this.dialog) {
                    this.saved.emit(true);
                } else {
                    this.messageService.infoMessage('Die Bemerkung wurde hinzugefügt');
                    this.loadTier();
                }
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Fehler beim Speichern der Bemerkungen!', err);
            },
        });
    }

    private loadTier(): void {
        this.gruppeTierSubscription?.unsubscribe();
        this.isLoading = true;
        this.gruppeTierSubscription = this.gruppenService.getGruppeTier(this.tier?.gt_id).subscribe({
            next: tier => {
                this.tier = tier;
                if (!this.tier.bemerkungen) {
                    this.tier.bemerkungen = [];
                }
                this.loadOptionen();
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden des Tiers', err);
                this.isLoading = false;
            },
        });
    }

    private loadOptionen(): void {
        this.optionenSub?.unsubscribe();
        this.optionenSub = this.gruppeTierService.loadLetzteBemerkungen().subscribe({
            next: texte => {
                const existing = (this.tier?.bemerkungen ?? [])
                    .map(bemerkung => (bemerkung?.bemerkung ?? '').trim())
                    .filter(text => text.length > 0);
                this.initialTexts = new Set(existing);
                const optionen: BemerkungOption[] = [];
                const seen = new Set<string>();
                for (const text of [...texte, ...existing]) {
                    if (!text || seen.has(text)) {
                        continue;
                    }
                    seen.add(text);
                    optionen.push({ text, checked: this.initialTexts.has(text) });
                }
                this.optionen = optionen;
                this.isLoading = false;
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Bemerkungen konnten nicht geladen werden!', err);
            },
        });
    }
}
